/** 对客报价后端访问层，统一经 httpClient 出口并解信封。 */

import type { ApiEnvelope } from "../../../lib/http/contracts";
import type { components } from "../../../api/generated";
import { httpClient } from "../../../lib/http/axios";
import {
  mapCustomerQuoteAllocationRequest,
  mapCustomerQuoteAllocationResult,
  mapCustomerQuoteEditor,
  mapCustomerQuotePreview,
  mapCustomerQuoteSnapshot,
  mapCustomerQuoteSnapshotPayload,
  mapCustomerQuoteSourceChangeCommandPayload,
  mapEnterCustomerQuoteResult,
  mapSaveCustomerQuoteDraftPayload,
  type QuoteAllocationResultContract,
} from "../api/adapters";
import type {
  CustomerQuoteAllocationResult,
  CustomerQuoteEditorData,
  CustomerQuotePreview,
  CustomerQuoteSnapshot,
  CustomerQuoteSourceChangeCommand,
  CustomerQuoteConfirmSourceChangeCommand,
  EnterCustomerQuoteResult,
  SaveCustomerQuoteDraftRequest,
} from "../types";

type Raw = Record<string, unknown>;

/** 根据当前草稿反算目标，结果需由用户保存。 */
export async function repriceCustomerQuoteTarget(
  customerQuoteId: string, snapshot: CustomerQuoteSnapshot,
  targetKind: "per_student_difference" | "margin_rate", targetAmount: string,
  command: Pick<SaveCustomerQuoteDraftRequest, "expectedQuoteRevision" | "expectedSourceRevision">,
): Promise<CustomerQuoteSnapshot> {
  const response = await httpClient.post<ApiEnvelope<components["schemas"]["CustomerQuoteSnapshot-Output"]>>(
    `/customer-quotes/${customerQuoteId}/reprice-target`,
    { expected_quote_revision: command.expectedQuoteRevision, expected_source_revision: command.expectedSourceRevision, snapshot: mapCustomerQuoteSnapshotPayload(snapshot), target_kind: targetKind, target_amount: targetAmount },
  );
  return mapCustomerQuoteSnapshot(unwrap(response.data));
}

/** 按精确报价恢复冻结订单名单，未绑定时保留空上下文。 */
export async function fetchCustomerQuoteFeeAdjustmentContext(
  customerQuoteId: string,
): Promise<{ orderId: string; rosterId: string } | null> {
  const response = await httpClient.get<ApiEnvelope<{ order_id: string; roster_id: string } | null>>(
    `/fee-adjustments/customer-quotes/${customerQuoteId}/context`,
  );
  if (response.data.code === "OK" && response.data.data === null) return null;
  const context = unwrap(response.data);
  return context === null ? null : { orderId: context.order_id, rosterId: context.roster_id };
}

/** 解统一响应信封，业务失败直接抛错。 */
function unwrap<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.code !== "OK" || envelope.data === null || envelope.data === undefined) {
    throw new Error(`对客报价请求失败：${envelope.code}`);
  }
  return envelope.data;
}

/** 按精确已定稿核价 ID 创建或打开报价。 */
export async function enterCustomerQuote(budgetOrderId: string): Promise<EnterCustomerQuoteResult> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/customer-quotes/budgets/${budgetOrderId}/enter`,
  );
  return mapEnterCustomerQuoteResult(unwrap(response.data));
}

/** 查询报价编辑器完整数据。 */
export async function fetchCustomerQuoteEditor(
  customerQuoteId: string,
): Promise<CustomerQuoteEditorData> {
  const response = await httpClient.get<ApiEnvelope<Raw>>(
    `/customer-quotes/${customerQuoteId}/editor`,
  );
  return mapCustomerQuoteEditor(unwrap(response.data));
}

/** 使用当前快照试算；传入已保存版本上下文时同步保存标准报价结果。 */
export async function calculateCustomerQuoteAllocation(
  snapshot: CustomerQuoteSnapshot,
  persistence?: { customerQuoteId: string; expectedQuoteRevision: string },
): Promise<CustomerQuoteAllocationResult> {
  const response = await httpClient.post<ApiEnvelope<QuoteAllocationResultContract>>(
    "/quote-allocations/calculate",
    mapCustomerQuoteAllocationRequest(snapshot, persistence),
  );
  return mapCustomerQuoteAllocationResult(unwrap(response.data));
}

/** 保存报价草稿，服务端重新校验来源快照并计算汇总。 */
export async function saveCustomerQuoteDraft(
  customerQuoteId: string,
  request: SaveCustomerQuoteDraftRequest,
): Promise<CustomerQuoteEditorData> {
  const response = await httpClient.put<ApiEnvelope<Raw>>(
    `/customer-quotes/${customerQuoteId}/draft`,
    mapSaveCustomerQuoteDraftPayload(request),
  );
  return mapCustomerQuoteEditor(unwrap(response.data));
}

/** 校验并定稿当前报价版本。 */
export async function finalizeCustomerQuote(
  customerQuoteId: string,
  request: CustomerQuoteSourceChangeCommand,
): Promise<CustomerQuoteEditorData> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/customer-quotes/${customerQuoteId}/finalize`,
    mapCustomerQuoteSourceChangeCommandPayload(request),
  );
  return mapCustomerQuoteEditor(unwrap(response.data));
}

/** 确认当前核价的一项来源差异。 */
export async function confirmCustomerQuoteSourceChange(
  customerQuoteId: string,
  changeId: string,
  request: CustomerQuoteConfirmSourceChangeCommand,
): Promise<CustomerQuoteEditorData> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/customer-quotes/${customerQuoteId}/source-changes/${changeId}/confirm`,
    {
      ...mapCustomerQuoteSourceChangeCommandPayload(request),
      ...(request.baseMarkup == null ? {} : { base_markup: request.baseMarkup }),
      ...(request.financeTreatment === undefined ? {} : { finance_treatment: request.financeTreatment }),
    },
  );
  return mapCustomerQuoteEditor(unwrap(response.data));
}

/** 按最新核价重算当前草稿的 16% 自动报价，不创建新版本。 */
export async function generateCustomerQuoteAutoTarget(
  customerQuoteId: string,
  request: CustomerQuoteSourceChangeCommand,
): Promise<CustomerQuoteEditorData> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/customer-quotes/${customerQuoteId}/auto-target`,
    mapCustomerQuoteSourceChangeCommandPayload(request),
  );
  return mapCustomerQuoteEditor(unwrap(response.data));
}

/** 使用当前未保存草稿生成客户安全预览。 */
export async function previewCustomerQuote(
  customerQuoteId: string,
  snapshot: CustomerQuoteSnapshot,
): Promise<CustomerQuotePreview> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/customer-quotes/${customerQuoteId}/preview`,
    { snapshot: mapCustomerQuoteSnapshotPayload(snapshot) },
  );
  return mapCustomerQuotePreview(unwrap(response.data));
}

/** 使用当前未保存草稿获取可复制的客户报价 Markdown。 */
export async function exportCustomerQuoteMarkdown(
  customerQuoteId: string,
  snapshot: CustomerQuoteSnapshot,
): Promise<string> {
  const response = await httpClient.post<ApiEnvelope<Raw>>(
    `/customer-quotes/${customerQuoteId}/export.md`,
    { snapshot: mapCustomerQuoteSnapshotPayload(snapshot) },
  );
  return String(unwrap(response.data).markdown ?? "");
}

export type CustomerQuoteExcelDownload = {
  blob: Blob;
  fileName: string;
};

/** 获取使用当前安全快照生成的客户报价 Excel 下载结果。 */
export async function fetchCustomerQuoteExcelDownload(
  customerQuoteId: string,
  quoteName: string,
  snapshot: CustomerQuoteSnapshot,
): Promise<CustomerQuoteExcelDownload> {
  const response = await httpClient.post<Blob>(
    `/customer-quotes/${customerQuoteId}/export.xlsx`,
    {
      quote_name: quoteName,
      snapshot: mapCustomerQuoteSnapshotPayload(snapshot),
    },
    { responseType: "blob" },
  );
  const disposition = String(response.headers["content-disposition"] ?? "");
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  let fileName = `${quoteName.trim() || "客户报价"}.xlsx`;
  if (encodedName) {
    try {
      fileName = decodeURIComponent(encodedName.replace(/^"|"$/g, ""));
    } catch {
      // 无法解码时沿用安全兜底文件名。
    }
  }
  return { blob: response.data, fileName };
}

/** 显式触发已确认有效的客户报价 Excel 浏览器下载。 */
export function triggerCustomerQuoteExcelDownload(
  download: CustomerQuoteExcelDownload,
): void {
  const url = window.URL.createObjectURL(download.blob);
  const link = document.createElement("a");
  try {
    link.href = url;
    link.download = download.fileName;
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}
